import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { generateClient, post } from "aws-amplify/api";
import { createUserChecklistProgress } from "../graphql/mutations";
import { userChecklistProgressesByUserId } from "../graphql/queries";

const client = generateClient();

export interface UserChecklistProgress {
  id: string;
  userId: string;
  welcomeShown: boolean;
  loginDates: string[];
  shippingAddress: string | null;
  step1Complete: boolean;
  step2Complete: boolean;
  step3Complete: boolean;
  step4Complete: boolean;
  step5Complete: boolean;
  step6Complete: boolean;
  hatUnlocked: boolean;
  createdAt: string;
  updatedAt: string;
  owner: string;
}

export interface ShippingForm {
  name: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}

export interface ClubChecklistState {
  loading: "idle" | "pending" | "success" | "failed";
  error: string | null;
  data: UserChecklistProgress | null;
}

const initialState: ClubChecklistState = {
  loading: "idle",
  error: null,
  data: null,
};

// Helper: call checklistapi REST endpoint
const callChecklistApi = async (path: string, body: object = {}) => {
  const restOperation = post({
    apiName: "checklistapi",
    path,
    options: { body: body as any },
  });
  const response = await restOperation.response;
  const json = (await response.body.json()) as any;
  return json.data as UserChecklistProgress;
};

// Helper: get typed state from thunkAPI
const getChecklistState = (thunkAPI: any): UserChecklistProgress | null =>
  (thunkAPI.getState() as any).clubChecklist.data;

// Helper: extract error message from unknown catch (handles Amplify GraphQL error objects)
const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) return error.message;
  if (typeof error === "object" && error !== null) {
    const e = error as any;
    if (Array.isArray(e.errors) && e.errors[0]?.message) return e.errors[0].message;
    if (typeof e.message === "string") return e.message;
  }
  return String(error);
};

// load existing checklist or create new one if not exist for the user
export const fetchOrCreateChecklist = createAsyncThunk<
  UserChecklistProgress,
  string,
  { rejectValue: string }
>("clubChecklist/fetchOrCreate", async (userId, thunkAPI) => {
  try {
    const result = (await client.graphql({
      query: userChecklistProgressesByUserId,
      variables: { userId, limit: 1 },
    })) as any;
    const items = result.data.userChecklistProgressesByUserId.items;
    if (items.length > 0) return items[0];

    const created = (await client.graphql({
      query: createUserChecklistProgress,
      variables: { input: { userId, loginDates: [] } },
    })) as any;
    return created.data.createUserChecklistProgress;
  } catch (error) {
    return thunkAPI.rejectWithValue(getErrorMessage(error));
  }
});

// step 5 is to login for 3 different days, so we record login date each time and check if step 5 is complete
export const recordLoginDate = createAsyncThunk<
  UserChecklistProgress | null,
  void,
  { rejectValue: string }
>("clubChecklist/recordLoginDate", async (_, thunkAPI) => {
  try {
    const checklist = getChecklistState(thunkAPI);
    if (!checklist) return null;

    // Client-side idempotency guard to avoid unnecessary Lambda calls
    const today = new Date().toISOString().split("T")[0];
    const existing = checklist.loginDates || [];
    if (existing.includes(today)) return checklist;

    return await callChecklistApi("/checklist/record-login");
  } catch (error) {
    return thunkAPI.rejectWithValue(getErrorMessage(error));
  }
});

// steps 1,2,3 are for trying out different features, so we mark them complete via Lambda
export const markStepComplete = createAsyncThunk<
  UserChecklistProgress | null,
  number,
  { rejectValue: string }
>("clubChecklist/markStepComplete", async (step, thunkAPI) => {
  try {
    const checklist = getChecklistState(thunkAPI);
    if (!checklist) return null;

    const stepKey = `step${step}Complete` as keyof UserChecklistProgress;
    if (checklist[stepKey]) return checklist; // already done

    return await callChecklistApi("/checklist/complete-step", { step });
  } catch (error) {
    return thunkAPI.rejectWithValue(getErrorMessage(error));
  }
});

// marks welcome screen as shown
export const markWelcomeShown = createAsyncThunk<
  UserChecklistProgress | null,
  void,
  { rejectValue: string }
>("clubChecklist/markWelcomeShown", async (_, thunkAPI) => {
  try {
    const checklist = getChecklistState(thunkAPI);
    if (!checklist || checklist.welcomeShown) return checklist ?? null;

    return await callChecklistApi("/checklist/mark-welcome-shown");
  } catch (error) {
    return thunkAPI.rejectWithValue(getErrorMessage(error));
  }
});

// saves shipping address for hat fulfilment
export const saveShippingAddress = createAsyncThunk<
  UserChecklistProgress | null,
  ShippingForm,
  { rejectValue: string }
>("clubChecklist/saveShippingAddress", async (address, thunkAPI) => {
  try {
    const checklist = getChecklistState(thunkAPI);
    if (!checklist) return null;

    return await callChecklistApi("/checklist/save-shipping", { address });
  } catch (error) {
    return thunkAPI.rejectWithValue(getErrorMessage(error));
  }
});

const clubChecklistSlice = createSlice({
  name: "clubChecklist",
  initialState,
  reducers: {
    updateClubChecklistData(state, action: { payload: UserChecklistProgress }) {
      state.data = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOrCreateChecklist.pending, (state) => {
        state.loading = "pending";
        state.error = null;
      })
      .addCase(fetchOrCreateChecklist.fulfilled, (state, action) => {
        state.loading = "success";
        state.data = action.payload;
      })
      .addCase(fetchOrCreateChecklist.rejected, (state, action) => {
        state.loading = "failed";
        state.error = action.payload ?? action.error.message ?? null;
      })
      .addCase(recordLoginDate.fulfilled, (state, action) => {
        if (action.payload) state.data = action.payload;
        state.error = null;
      })
      .addCase(recordLoginDate.rejected, (state, action) => {
        state.error = action.payload ?? action.error.message ?? null;
      })
      .addCase(markStepComplete.fulfilled, (state, action) => {
        if (action.payload) state.data = action.payload;
        state.error = null;
      })
      .addCase(markStepComplete.rejected, (state, action) => {
        state.error = action.payload ?? action.error.message ?? null;
      })
      .addCase(markWelcomeShown.fulfilled, (state, action) => {
        if (action.payload) state.data = action.payload;
        state.error = null;
      })
      .addCase(markWelcomeShown.rejected, (state, action) => {
        state.error = action.payload ?? action.error.message ?? null;
      })
      .addCase(saveShippingAddress.fulfilled, (state, action) => {
        if (action.payload) state.data = action.payload;
        state.error = null;
      })
      .addCase(saveShippingAddress.rejected, (state, action) => {
        state.error = action.payload ?? action.error.message ?? null;
      });
  },
});

export const { updateClubChecklistData } = clubChecklistSlice.actions;
export default clubChecklistSlice.reducer;
